import React from 'react';
import { Alert, Typography, Button, Space, Grid, notification, Popconfirm } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser } from '@/store/account/selector';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { updateUserAccount } from '@/store/account/slice';
import { useMutation } from '@tanstack/react-query';
import { renewSubscription } from '@/api/novi/user';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const NoviSubCancelPromo: React.FC = () => {
	const screens = useBreakpoint();
	const dispatch = useAppDispatch();
	const loggedInUser = useAppSelector(getLoggedInUser);

	const renewSubscriptionMutation = useMutation({
		mutationKey: ['renew-subscription'],
		mutationFn: renewSubscription,
		onSuccess: () => {
			notification.success({
				message: 'Subscription renewed successfully',
			});
			dispatch(updateUserAccount({ userAccount: { outboundScheduleSubCancelOn: undefined } }));
		},
		onError: (error: any) => {
			notification.error({
				message: error?.message || 'Something went wrong',
			});
		},
	});

	if (!loggedInUser?.outboundScheduleSubCancelOn) {
		return null;
	}

	const handleRenewClick = () => {
		renewSubscriptionMutation.mutate();
	};

	const cancelDate = new Date(
		loggedInUser?.outboundScheduleSubCancelOn as string,
	).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});

	return (
		<Alert
			type="warning"
			showIcon
			icon={<WarningOutlined />}
			closable
			message="Subscription Cancellation Notice"
			description={
				<Space direction="vertical" size={screens.md ? 12 : 8} style={{ width: '100%' }}>
					<div>
						<Text style={{ fontSize: screens.md ? '16px' : '14px' }}>
							Your subscription is scheduled to be canceled on <Text strong>{cancelDate}</Text>
						</Text>
					</div>
					<div>
						<Text type="secondary" style={{ fontSize: screens.md ? '14px' : '12px' }}>
							To keep your access uninterrupted, you can renew your subscription before the
							cancellation date by clicking on the Renew Subscription button.
						</Text>
					</div>
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
						}}
					>
						<Popconfirm
							title="Renew Subscription"
							description={`This will cancel the scheduled cancellation date of ${cancelDate}. Are you sure you want to continue?`}
							okText="Yes, Renew"
							cancelText="No, Keep Cancellation"
							onConfirm={handleRenewClick}
							okButtonProps={{
								loading: renewSubscriptionMutation.isPending,
							}}
						>
							<Button
								type="primary"
								size="middle"
								style={{
									width: screens.md ? 'auto' : '100%',
								}}
								loading={renewSubscriptionMutation.isPending}
								disabled={renewSubscriptionMutation.isPending}
							>
								Renew Subscription
							</Button>
						</Popconfirm>
					</div>
				</Space>
			}
			style={{
				border: '1px solid #faad14',
				borderRadius: '6px',
				padding: screens.md ? '16px' : '12px',
			}}
		/>
	);
};

export default NoviSubCancelPromo;
